// frontend/src/hooks/useFormulariosDependientes.js
import { useState, useEffect, useCallback } from 'react';
import formularioService from '../services/formularioService';

const useFormulariosDependientes = (historiaId, especialidadId) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formulariosPadre, setFormulariosPadre] = useState([]);
  const [formulariosHijos, setFormulariosHijos] = useState({});
  const [analisisProgreso, setAnalisisProgreso] = useState({});
  
  // Cargar todos los formularios con sus dependencias
  const cargarFormularios = useCallback(async () => {
    if (!historiaId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Obtener todas las respuestas
      const respuestas = await formularioService.getRespuestasPorHistoriaEspecialidad(historiaId);
      
      if (!respuestas.data || !Array.isArray(respuestas.data)) {
        throw new Error('No se pudieron cargar los formularios');
      }
      
      // Separar padres e hijos
      const padres = [];
      const hijos = {};
      const padresMap = new Map();
      
      // Primera pasada: identificar padres
      respuestas.data.forEach(respuesta => {
        if (respuesta.formulario?.tipo_dependencia === 'padre') {
          padres.push(respuesta);
          padresMap.set(respuesta.id, respuesta);
          hijos[respuesta.id] = [];
        }
      });
      
      // Segunda pasada: asociar hijos
      respuestas.data.forEach(respuesta => {
        if (respuesta.parent_respuesta_id && hijos[respuesta.parent_respuesta_id]) {
          hijos[respuesta.parent_respuesta_id].push(respuesta);
        }
      });
      
      // Ordenar padres por fecha
      padres.sort((a, b) => new Date(b.fecha_completado) - new Date(a.fecha_completado));
      
      // Ordenar hijos por fecha
      Object.keys(hijos).forEach(padreId => {
        hijos[padreId].sort((a, b) => 
          new Date(a.fecha_completado) - new Date(b.fecha_completado)
        );
      });
      
      setFormulariosPadre(padres);
      setFormulariosHijos(hijos);
      
      // Cargar análisis de progreso para cada padre con hijos
      const analisisPromises = padres
        .filter(p => hijos[p.id]?.length > 0)
        .map(async (padre) => {
          try {
            const resultado = await formularioService.getAnalisisProgreso(padre.id);
            return { padreId: padre.id, analisis: resultado.data };
          } catch (err) {
            console.error(`Error al cargar análisis para ${padre.id}:`, err);
            return { padreId: padre.id, analisis: null };
          }
        });
      
      const analisisResultados = await Promise.all(analisisPromises);
      const analisisMap = {};
      analisisResultados.forEach(({ padreId, analisis }) => {
        if (analisis) {
          analisisMap[padreId] = analisis;
        }
      });
      
      setAnalisisProgreso(analisisMap);
      
    } catch (err) {
      console.error('Error al cargar formularios:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [historiaId]);
  
  // Verificar si se puede crear un formulario hijo
  const puedeCrearHijo = useCallback(async (formularioPadreId) => {
    try {
      const resultado = await formularioService.puedeTenerHijos(formularioPadreId);
      return resultado.data?.puede_tener_hijos || false;
    } catch (error) {
      console.error('Error al verificar si puede crear hijo:', error);
      return false;
    }
  }, []);
  
  // Obtener formularios hijos disponibles para un padre
  const obtenerFormulariosHijosDisponibles = useCallback(async (formularioPadreId) => {
    try {
      const resultado = await formularioService.getFormulariosHijos(formularioPadreId);
      return resultado.data?.formulariosHijos || [];
    } catch (error) {
      console.error('Error al obtener formularios hijos disponibles:', error);
      return [];
    }
  }, []);
  
  // Verificar bloqueos en tiempo real
  const verificarBloqueos = useCallback((formularioHijo, historialSeguimiento) => {
    if (!formularioHijo.reglas_bloqueo || !historialSeguimiento.length) {
      return { bloqueado: false };
    }
    
    const reglas = typeof formularioHijo.reglas_bloqueo === 'string' ? 
      JSON.parse(formularioHijo.reglas_bloqueo) : formularioHijo.reglas_bloqueo;
    
    // Implementar verificación según tipo
    if (formularioHijo.tipo_dependencia === 'hijo_cualitativo') {
      return verificarBloqueoCualitativo(reglas, historialSeguimiento);
    } else if (formularioHijo.tipo_dependencia === 'hijo_cuantitativo') {
      return verificarBloqueoCuantitativo(reglas, historialSeguimiento);
    }
    
    return { bloqueado: false };
  }, []);
  
  const verificarBloqueoCualitativo = (reglas, historial) => {
    for (const regla of reglas.reglas) {
      if (regla.tipo === 'consecutivos_en_proceso') {
        const ultimosN = historial.slice(-regla.valor);
        
        if (ultimosN.length === regla.valor) {
          const todosEnProceso = ultimosN.every(s => 
            s.respuestas?.estado_progreso === regla.valor_campo
          );
          
          if (todosEnProceso) {
            return {
              bloqueado: true,
              motivo: regla.mensaje,
              requiereAnalisis: regla.requiere_observacion
            };
          }
        }
      }
    }
    
    return { bloqueado: false };
  };
  
  const verificarBloqueoCuantitativo = (reglas, historial) => {
    for (const regla of reglas.reglas) {
      if (regla.tipo === 'puntaje_consecutivo') {
        for (const condicion of regla.condiciones) {
          const ultimosN = historial.slice(-condicion.consecutivos);
          
          if (ultimosN.length === condicion.consecutivos) {
            const todosCumplen = ultimosN.every(s => {
              const evaluaciones = Object.values(s.respuestas?.objetivos_mensuales || {});
              if (evaluaciones.length === 0) return false;
              
              const promedios = evaluaciones.map(e => e.promedio || 0);
              const promedioGeneral = promedios.reduce((a, b) => a + b, 0) / promedios.length;
              
              return Math.round(promedioGeneral) === condicion.valor;
            });
            
            if (todosCumplen) {
              return {
                bloqueado: true,
                motivo: condicion.mensaje,
                accion: condicion.accion
              };
            }
          }
        }
      }
      
      if (regla.tipo === 'tiempo_limite' && historial.length > 0) {
        const primeraSesion = historial[0];
        const diasTranscurridos = Math.floor(
          (new Date() - new Date(primeraSesion.fecha_completado)) / (1000 * 60 * 60 * 24)
        );
        
        if (diasTranscurridos >= regla.dias) {
          return {
            bloqueado: true,
            motivo: regla.mensaje
          };
        }
      }
    }
    
    return { bloqueado: false };
  };
  
  // Calcular estadísticas de progreso
  const calcularEstadisticas = useCallback((formularioPadre, hijos) => {
    if (!hijos || hijos.length === 0) {
      return {
        totalSesiones: 0,
        objetivosTrabajados: 0,
        objetivosLogrados: 0,
        porcentajeProgreso: 0,
        estado: 'sin_iniciar'
      };
    }
    
    const stats = {
      totalSesiones: hijos.length,
      objetivosTrabajados: new Set(),
      objetivosLogrados: 0,
      objetivosEnProceso: 0,
      ultimaFecha: null,
      tendencia: 'estable'
    };
    
    // Analizar según tipo
    if (hijos[0]?.formulario?.tipo_dependencia === 'hijo_cualitativo') {
      hijos.forEach(hijo => {
        const objetivo = hijo.objetivos_seleccionados?.[0] || 
                        hijo.respuestas?.objetivo_seleccionado;
        
        if (objetivo) {
          stats.objetivosTrabajados.add(objetivo);
          
          if (hijo.respuestas?.estado_progreso === 'logrado') {
            stats.objetivosLogrados++;
          } else if (hijo.respuestas?.estado_progreso === 'en_proceso') {
            stats.objetivosEnProceso++;
          }
        }
        
        if (!stats.ultimaFecha || new Date(hijo.fecha_completado) > stats.ultimaFecha) {
          stats.ultimaFecha = new Date(hijo.fecha_completado);
        }
      });
    } else if (hijos[0]?.formulario?.tipo_dependencia === 'hijo_cuantitativo') {
      // Análisis para cuantitativo
      const ultimoMes = hijos[hijos.length - 1];
      const evaluaciones = Object.values(ultimoMes.respuestas?.objetivos_mensuales || {});
      
      evaluaciones.forEach(evaluacion => {
        if (evaluacion.observacion === 'Logrado') stats.objetivosLogrados++;
        else if (evaluacion.observacion === 'En Proceso') stats.objetivosEnProceso++;
      });
      
      stats.objetivosTrabajados = new Set(Object.keys(ultimoMes.respuestas?.objetivos_mensuales || {}));
    }
    
    // Calcular porcentaje
    const totalObjetivos = stats.objetivosTrabajados.size;
    stats.porcentajeProgreso = totalObjetivos > 0 ? 
      Math.round((stats.objetivosLogrados / totalObjetivos) * 100) : 0;
    
    // Determinar estado general
    if (stats.porcentajeProgreso >= 80) stats.estado = 'completado';
    else if (stats.porcentajeProgreso >= 50) stats.estado = 'avanzado';
    else if (stats.totalSesiones > 0) stats.estado = 'en_progreso';
    else stats.estado = 'sin_iniciar';
    
    return stats;
  }, []);
  
  // Efecto para cargar datos iniciales
  useEffect(() => {
    cargarFormularios();
  }, [cargarFormularios]);
  
  return {
    loading,
    error,
    formulariosPadre,
    formulariosHijos,
    analisisProgreso,
    puedeCrearHijo,
    obtenerFormulariosHijosDisponibles,
    verificarBloqueos,
    calcularEstadisticas,
    recargar: cargarFormularios
  };
};

export default useFormulariosDependientes;